import { DollarSign, Calendar, ShieldCheck, Award, ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation } from "@/lib/locations";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const features = [
  {
    icon: <DollarSign className="h-5 w-5 md:h-8 md:w-8 text-primary" />,
    title: "Upfront Pricing",
    description: "Know your exact price before you book.",
    delay: "delay-100"
  },
  {
    icon: <Calendar className="h-5 w-5 md:h-8 md:w-8 text-primary" />,
    title: "Easy Online Booking",
    description: "Book in under 60 seconds.",
    delay: "delay-200"
  },
  {
    icon: <ShieldCheck className="h-5 w-5 md:h-8 md:w-8 text-primary" />,
    title: "Trusted Local Cleaners",
    description: "Reliable, Insured, Professional.",
    delay: "delay-300"
  },
  {
    icon: <Award className="h-5 w-5 md:h-8 md:w-8 text-primary" />,
    title: "200% Satisfaction",
    description: "If missed, we fix it for free.",
    delay: "delay-400"
  }
];

export const WhyChooseUs = () => {
  const location = useLocation();
  const { ref: headingRef, isVisible: headingVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <section className="py-24 bg-muted/20">
      <div className="container">
        <div
          ref={headingRef}
          className={cn("text-center mb-16 anim-fade-up", headingVisible && "is-visible")}
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Why Uplevel Cleaning?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            We've built our service around transparency, convenience, and quality to give you the best experience in {location.name}.
          </p>
        </div>
        
        <div className="relative">
          <div ref={cardsRef} className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-8">
            {features.map((feature, i) => (
              <div
                key={i}
                className={cn(
                  "bg-background p-4 md:p-8 rounded-2xl border shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default anim-fade-up flex flex-col items-center text-center md:items-start md:text-left",
                  feature.delay,
                  cardsVisible && "is-visible"
                )}
              >
                <div className="mb-3 md:mb-6 inline-flex p-2.5 md:p-3 rounded-xl bg-primary/5 text-primary group-hover:bg-primary/10 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-sm md:text-xl font-bold mb-1.5 md:mb-3 leading-tight">{feature.title}</h3>
                <p className="text-muted-foreground text-[11px] md:text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div
          ref={ctaRef}
          className={cn(
            "mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 anim-fade-up",
            ctaVisible && "is-visible"
          )}
        >
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold text-lg px-12 py-8 h-auto rounded-2xl shadow-xl hover:shadow-2xl transition-all group w-full sm:w-auto">
            <a href={BOOKING_URL}>
              <Calendar className="mr-2 h-6 w-6" />
              Book Your Cleaning
              <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
            </a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 font-bold text-lg px-12 py-8 h-auto rounded-2xl w-full sm:w-auto">
            <a href={BOOKING_URL} aria-label="Get instant price and guarantee">
              <Zap className="mr-2 h-6 w-6 fill-primary/20" />
              Get Instant Price
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};
